import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Eye, EyeOff, BarChart3, Users, BookOpen, ShieldCheck, Lock, Target } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Show session expired message if redirected due to token expiration
  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      toast.error('Your session has expired. Please log in again.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col lg:flex-row"
      data-testid="login-page"
      style={{ backgroundColor: '#0a1628' }}
    >
      {/* ============================================================
         LEFT PANEL — Hero / Earth visual
         ============================================================ */}
      <div className="lg:w-[55%] relative overflow-hidden flex items-center px-8 sm:px-12 lg:px-20 py-16 lg:py-0">
        {/* Background hero image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(/login-hero.png)',
          }}
        />
        {/* Dark overlay so text remains readable */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(105deg, rgba(10,22,40,0.92) 0%, rgba(10,22,40,0.78) 45%, rgba(10,22,40,0.45) 100%)',
          }}
        />

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-xl"
        >
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 tracking-tight pb-2"
            style={{
              background:
                'linear-gradient(135deg, #FFFFFF 0%, #B6E5F4 60%, #23ACD5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: 1.18,
            }}
            data-testid="login-hero-headline"
          >
            Smarter Insurance.
            <br />
            Stronger Tomorrow.
          </h1>

          <p
            className="text-base sm:text-lg max-w-lg mb-12 leading-relaxed"
            style={{ color: 'rgba(214, 234, 248, 0.78)' }}
          >
            Data-driven insights. Intelligent decisions. Better outcomes for
            your business and your clients.
          </p>

          {/* Feature blocks */}
          <div className="space-y-7 mb-14 max-w-md">
            <FeatureRow
              icon={BarChart3}
              title="Analytics"
              description="Track production, growth, and team performance in real time."
            />
            <FeatureRow
              icon={Users}
              title="Agency Management"
              description="Onboard agents, manage hierarchy, and streamline operations."
            />
            <FeatureRow
              icon={BookOpen}
              title="Countless Resources"
              description="Training, scripts, carriers, and tools — all in one place."
            />
          </div>

          {/* Trust strip */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 pt-6 border-t border-cyan-400/15">
            <TrustItem icon={ShieldCheck} label="Trusted." sublabel="Secure." />
            <TrustItem icon={Lock} label="Built for Professionals." />
            <TrustItem icon={Target} label="Focused on Outcomes." />
          </div>
        </motion.div>
      </div>

      {/* ============================================================
         RIGHT PANEL — Login card
         ============================================================ */}
      <div
        className="lg:w-[45%] flex items-center justify-center px-6 sm:px-10 py-16 lg:py-0 relative"
        style={{
          background:
            'linear-gradient(180deg, #0d1c33 0%, #0a1628 100%)',
        }}
      >
        {/* Subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at center, #23ACD5 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="w-full max-w-md relative z-10"
        >
          {/* Brand logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/breeze-logo.png"
              alt="Breeze Financial Group"
              className="h-24 sm:h-28 w-auto select-none"
              draggable={false}
              data-testid="login-brand-logo"
            />
          </div>

          {/* Welcome heading */}
          <div className="text-center mb-8">
            <h2
              className="text-3xl font-bold mb-2"
              style={{ color: '#FFFFFF' }}
              data-testid="login-welcome-title"
            >
              Welcome back
            </h2>
            <p className="text-sm" style={{ color: 'rgba(214,234,248,0.6)' }}>
              Sign in to access your ATLAS dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {/* Email */}
            <div>
              <Label
                htmlFor="email"
                className="text-sm font-medium block mb-2"
                style={{ color: 'rgba(214,234,248,0.85)' }}
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@breezefg.com"
                data-testid="login-email-input"
                className="h-12 rounded-lg px-4 text-white placeholder:text-slate-500 focus:ring-2"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(35,172,213,0.18)',
                }}
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label
                  htmlFor="password"
                  className="text-sm font-medium"
                  style={{ color: 'rgba(214,234,248,0.85)' }}
                >
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium transition-colors hover:opacity-80"
                  style={{ color: '#23ACD5' }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••••••"
                  data-testid="login-password-input"
                  className="h-12 rounded-lg px-4 pr-12 text-white placeholder:text-slate-500 focus:ring-2"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(35,172,213,0.18)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-white/5 transition"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid="login-password-toggle"
                  style={{ color: 'rgba(214,234,248,0.55)' }}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Sign In button */}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 rounded-lg text-base font-semibold transition-all duration-200 disabled:opacity-70"
              style={{
                background:
                  'linear-gradient(180deg, #2BB7E0 0%, #1E8FB8 100%)',
                color: '#FFFFFF',
                boxShadow:
                  '0 8px 24px -8px rgba(35, 172, 213, 0.55), 0 0 0 1px rgba(35,172,213,0.35) inset',
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Footer links */}
          <p
            className="mt-8 text-center text-xs"
            style={{ color: 'rgba(214,234,248,0.45)' }}
          >
            By signing in, you agree to our{' '}
            <a
              href="#"
              className="hover:opacity-80 transition"
              style={{ color: 'rgba(214,234,248,0.7)' }}
              data-testid="login-terms-link"
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a
              href="#"
              className="hover:opacity-80 transition"
              style={{ color: 'rgba(214,234,248,0.7)' }}
              data-testid="login-privacy-link"
            >
              Privacy Policy
            </a>
            .
          </p>
        </motion.div>
      </div>
    </div>
  );
}

/* ----- Small presentation components ----- */

function FeatureRow({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-4">
      <div
        className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
        style={{
          background: 'rgba(35,172,213,0.10)',
          border: '1px solid rgba(35,172,213,0.30)',
        }}
      >
        <Icon className="h-5 w-5" style={{ color: '#23ACD5' }} />
      </div>
      <div>
        <h3
          className="font-semibold text-base mb-1"
          style={{ color: '#FFFFFF' }}
        >
          {title}
        </h3>
        <p
          className="text-sm leading-relaxed"
          style={{ color: 'rgba(214,234,248,0.65)' }}
        >
          {description}
        </p>
      </div>
    </div>
  );
}

function TrustItem({ icon: Icon, label, sublabel }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4" style={{ color: '#23ACD5' }} />
      <span
        className="text-xs font-medium tracking-wide"
        style={{ color: 'rgba(214,234,248,0.78)' }}
      >
        {label}
        {sublabel && (
          <span
            className="ml-1"
            style={{ color: 'rgba(214,234,248,0.55)' }}
          >
            {sublabel}
          </span>
        )}
      </span>
    </div>
  );
}
